import { z } from 'zod';

/**
 * Common validation schemas for admin forms
 */

// Base validations
export const requiredString = (fieldName: string, minLength = 1) =>
  z.string()
    .min(minLength, `${fieldName} é obrigatório`)
    .max(255, `${fieldName} não pode ter mais de 255 caracteres`)
    .trim();

export const optionalString = () =>
  z.string()
    .max(255, 'Texto não pode ter mais de 255 caracteres')
    .trim()
    .optional()
    .or(z.literal(''));

export const email = () =>
  z.string()
    .email('Email inválido')
    .max(254, 'Email muito longo');

export const positiveNumber = (fieldName: string) =>
  z.number()
    .min(0, `${fieldName} deve ser um número positivo`)
    .max(999999999, `${fieldName} muito grande`);

export const positiveInteger = (fieldName: string) =>
  z.number()
    .int(`${fieldName} deve ser um número inteiro`)
    .min(0, `${fieldName} deve ser um número positivo`)
    .max(999999999, `${fieldName} muito grande`);

export const uuid = () =>
  z.string()
    .uuid('ID inválido')
    .optional()
    .or(z.literal(''));

export const url = () =>
  z.string()
    .url('URL inválida')
    .optional()
    .or(z.literal(''));

// Specific schemas for admin entities

export const productSchema = z.object({
  name: requiredString('Nome do produto', 2),
  description: optionalString(),
  image: url(),
  price: positiveNumber('Preço'),
  pointsPrice: positiveInteger('Pontos necessários'),
  brandId: uuid(),
  categoryId: uuid(),
  subcategoryId: uuid(),
  stock: positiveInteger('Stock'),
  additionalImages: z.array(z.string().url()).optional().default([])
});

export const categorySchema = z.object({
  name: requiredString('Nome da categoria', 2),
  image: url(),
  parentId: uuid()
});

export const brandSchema = z.object({
  name: requiredString('Nome da marca', 2),
  logo: url(),
  description: optionalString()
});

export const storeSchema = z.object({
  name: requiredString('Nome da loja', 2),
  location: requiredString('Localização', 2),
  description: optionalString(),
  dartsbeat_key: optionalString()
});

export const userSchema = z.object({
  email: email(),
  full_name: requiredString('Nome completo', 2),
  username: requiredString('Nome de utilizador', 2),
  role: z.enum(['user', 'admin', 'moderator'], {
    errorMap: () => ({ message: 'Função inválida' })
  }),
  status: z.enum(['active', 'inactive', 'suspended'], {
    errorMap: () => ({ message: 'Estado inválido' })
  }),
  points: positiveInteger('Pontos'),
  main_store_id: uuid(),
  connected_account_id: optionalString()
});

/**
 * Form validation hook
 */
export const useFormValidation = <T>(schema: z.ZodSchema<T>) => {
  const validate = (data: unknown): { success: true; data: T } | { success: false; errors: Record<string, string> } => {
    try {
      const validData = schema.parse(data);
      return { success: true, data: validData };
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach(err => {
          const path = err.path.join('.');
          errors[path] = err.message;
        });
        return { success: false, errors };
      }
      return { success: false, errors: { general: 'Erro de validação desconhecido' } };
    }
  };

  const validateField = (fieldName: string, value: unknown): string | null => {
    try {
      schema.pick({ [fieldName]: true }).parse({ [fieldName]: value });
      return null;
    } catch (error) {
      if (error instanceof z.ZodError) {
        return error.errors[0]?.message || 'Erro de validação';
      }
      return 'Erro de validação';
    }
  };

  return { validate, validateField };
};

/**
 * Validation error display component helpers
 */
export const getFieldError = (errors: Record<string, string>, fieldName: string): string | undefined => {
  return errors[fieldName];
};

export const hasError = (errors: Record<string, string>, fieldName: string): boolean => {
  return !!errors[fieldName];
};

/**
 * File validation
 */
export const validateImageFile = (file: File): string | null => {
  // Check file type
  if (!file.type.startsWith('image/')) {
    return 'Por favor, seleccione apenas ficheiros de imagem';
  }

  // Check file size (5MB limit)
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    return 'O ficheiro é muito grande. Limite máximo: 5MB';
  }

  // Check file extension
  const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
  const fileName = file.name.toLowerCase();
  const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

  if (!hasValidExtension) {
    return 'Formato de ficheiro não suportado. Use: JPG, PNG, GIF ou WebP';
  }

  return null;
};

/**
 * Input sanitization for client-side
 */
export const sanitizeInput = (value: string): string => {
  return value
    .trim()
    .replace(/[<>]/g, '') // Remove potential XSS characters
    .replace(/[\x00-\x1f\x7f-\x9f]/g, '') // Remove control characters
    .substring(0, 1000); // Limit length
};

export type ProductFormData = z.infer<typeof productSchema>;
export type CategoryFormData = z.infer<typeof categorySchema>;
export type BrandFormData = z.infer<typeof brandSchema>;
export type StoreFormData = z.infer<typeof storeSchema>;
export type UserFormData = z.infer<typeof userSchema>;